/**
 * Reset password — ELF-CIRCLE.
 *
 * Three states: no token in the URL, the form, and the confirmation. The
 * `resetPassword` call and every validation rule are the template's; only the
 * presentation changed. See ElfAuthShell for the two-panel chrome.
 */

import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, Loader2, Eye, EyeOff, Check, AlertTriangle } from 'lucide-react';
import { SEO } from '../../../shared/components';
import { useAuthContext } from '../context/AuthContext';
import { ElfAuthShell } from '../components';
import { route } from '../../../shared/utils';

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState({ password: '', confirmPassword: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const { resetPassword } = useAuthContext();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token) {
      setMessage('Invalid or missing reset token');
      setIsSuccess(false);
      return;
    }
    if (!formData.password || !formData.confirmPassword) {
      setMessage('Please fill in all fields');
      setIsSuccess(false);
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setMessage('Passwords do not match');
      setIsSuccess(false);
      return;
    }
    if (formData.password.length < 8) {
      setMessage('Password must be at least 8 characters long');
      setIsSuccess(false);
      return;
    }

    setIsSubmitting(true);
    setMessage(null);

    try {
      const result = await resetPassword({ token, password: formData.password });
      if (result.success) {
        setIsSuccess(true);
        setMessage('Your password has been reset successfully');
      } else {
        setIsSuccess(false);
        setMessage(result.error || 'Failed to reset password. The link may have expired.');
      }
    } catch (error) {
      setIsSuccess(false);
      setMessage(
        error instanceof Error
          ? error.message
          : 'Failed to reset password. The link may have expired.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reached without a token — usually a truncated link out of an email client.
  if (!token) {
    return (
      <>
        <SEO title="Invalid reset link" description="This password reset link is not valid." />
        <ElfAuthShell
          kicker="Reset password"
          title="This link isn't valid"
          artHeadline="Links expire, on purpose"
          artLede="A reset link is single-use and time-limited. Requesting a new one takes a moment."
        >
          <div className="elf-auth__done">
            <span className="elf-auth__tick elf-auth__tick--warn" aria-hidden="true">
              <AlertTriangle />
            </span>
            <p className="elf-auth__note">
              This reset link is missing its token, which usually means the email client cut the
              URL short. Request a fresh one and open it directly from the email.
            </p>
            <div className="elf-auth__stack">
              <Link className="pillcta elf-auth__submit" to={route('/auth/forgot-password')}>
                Request a new link
                <span aria-hidden="true">
                  <ArrowRight />
                </span>
              </Link>
              <Link className="elf-auth__ghost" to={route('/auth/login')}>
                Back to sign in
              </Link>
            </div>
          </div>
        </ElfAuthShell>
      </>
    );
  }

  return (
    <>
      <SEO
        title="Set a new password"
        description="Choose a new password for your ELF-CIRCLE account."
        keywords="reset password, new password"
      />

      <ElfAuthShell
        kicker={isSuccess ? 'Done' : 'Reset password'}
        title={isSuccess ? 'Password updated' : 'Set a new password'}
        lede={isSuccess ? undefined : 'Eight characters minimum. Make it one you don’t reuse.'}
        artHeadline="One key, one door"
        artLede="Changing it here signs out nothing else — you keep working where you already are."
      >
        {isSuccess ? (
          <div className="elf-auth__done">
            <span className="elf-auth__tick" aria-hidden="true">
              <Check />
            </span>
            <p className="elf-auth__note">{message} You can sign in with it now.</p>
            <Link className="pillcta elf-auth__submit" to={route('/auth/login')}>
              Sign in
              <span aria-hidden="true">
                <ArrowRight />
              </span>
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="elf-auth__form" noValidate>
            {message && (
              <div className="elf-auth__err" role="alert">
                {message}
              </div>
            )}

            <label className="elf-auth__field">
              <span>New password</span>
              <span className="elf-auth__pwwrap">
                <input
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="At least 8 characters"
                />
                <button
                  type="button"
                  className="elf-auth__peek"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
                </button>
              </span>
            </label>

            <label className="elf-auth__field">
              <span>Confirm new password</span>
              <span className="elf-auth__pwwrap">
                <input
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  placeholder="Type it again"
                />
                <button
                  type="button"
                  className="elf-auth__peek"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  {showConfirmPassword ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
                </button>
              </span>
            </label>

            <button type="submit" className="pillcta elf-auth__submit" disabled={isSubmitting}>
              {isSubmitting ? 'Updating…' : 'Update password'}
              <span aria-hidden="true">
                {isSubmitting ? <Loader2 className="elf-auth__spin" /> : <ArrowRight />}
              </span>
            </button>

            <p className="elf-auth__alt">
              Changed your mind?{' '}
              <Link className="elf-auth__link" to={route('/auth/login')}>
                Back to sign in
              </Link>
            </p>
          </form>
        )}
      </ElfAuthShell>
    </>
  );
};

export default ResetPasswordPage;
